import React, { useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import {
  BaseFilterProps,
  Filter,
  useFilterContext,
  useMonthPickerFilter,
  MonthRangeFilterLayoutType,
} from 'framework/list';
import { FilterCondition } from 'framework/types';
import { DateInput } from 'framework/form';
import { GREATER_OR_EQUAL, LESS_OR_EQUAL, DATE_FORMAT } from 'framework/utils';
import { useUiTheme } from 'framework/uiTheme';
import { FloatingPlacement } from 'framework/ui/uiContract';
import { Typography } from '@mui/material';

// TODO move to shared folder when issue with Typography is fixed

interface MonthRangeFilterProps extends BaseFilterProps {
  dateFormat?: string;
  canBeInFuture?: boolean;
  label?: string;
  popperPlacement?: FloatingPlacement;
}

const MonthRangeFilter = ({
  source,
  dateFormat = DATE_FORMAT.API_ONLY_MONTH,
  canBeInFuture = false,
  label,
  popperPlacement,
}: MonthRangeFilterProps) => {
  const { MonthRangeFilterLayout } = useUiTheme<MonthRangeFilterLayoutType>();
  const { filterBy, getValue, filters } = useFilterContext();
  const [filterValue, setFilterValue] = useState(filters[source]);
  const { validateAndShowErrorMessages, getErrors, fromApiToFrontFormat, formatDateForAPI, getMaxDate, yearMonth } =
    useMonthPickerFilter(dateFormat, canBeInFuture, source);
  const MAX_DATE = getMaxDate();

  const hasError = !isEmpty([...getErrors(GREATER_OR_EQUAL), ...getErrors(LESS_OR_EQUAL)]);
  const invalidClass = hasError ? 'is-invalid' : '';

  const onChangeCallback = (filterPair: Dictionary<string>) => {
    const newFilterValue = filterPair;
    if (filterPair) {
      const inputName = Object.keys(filterPair)[0] as FilterCondition;
      const textDate = filterPair[inputName];
      const validationResult = validateAndShowErrorMessages(inputName, textDate);
      if (textDate && !validationResult) {
        if (inputName === GREATER_OR_EQUAL) {
          newFilterValue[inputName] = formatDateForAPI(textDate);
        }
        if (inputName === LESS_OR_EQUAL) {
          newFilterValue[inputName] = formatDateForAPI(textDate);
        }
      }
      setFilterValue(prevFilterValue => ({
        ...prevFilterValue,
        ...newFilterValue,
      }));
    }
  };

  useEffect(() => {
    if (filterValue) {
      validateAndShowErrorMessages(GREATER_OR_EQUAL, fromApiToFrontFormat(filterValue[GREATER_OR_EQUAL]));
      validateAndShowErrorMessages(LESS_OR_EQUAL, fromApiToFrontFormat(filterValue[LESS_OR_EQUAL]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const filterValueWithoutEmpty: Filter = {};

    Object.entries(filterValue || {}).forEach(entry => {
      const [condition, value] = entry;

      if (value) {
        filterValueWithoutEmpty[condition] = value;
      }
    });

    filterBy({
      [source]: filterValueWithoutEmpty,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue]);

  const leValue = getValue(source, LESS_OR_EQUAL);
  const geValue = getValue(source, GREATER_OR_EQUAL);

  return (
    <MonthRangeFilterLayout
      label={label}
      leValue={leValue}
      geValue={geValue}
      onChangeCallback={onChangeCallback}
      source={source}
      className={invalidClass}
      popperPlacement={popperPlacement}
    >
      <DateInput
        toggleButton={
          <>
            <i className="fa fa-calendar" aria-hidden style={{ marginRight: 1 }} />
            From:
          </>
        }
        label={label}
        source={GREATER_OR_EQUAL}
        onChangeCallback={onChangeCallback}
        value={fromApiToFrontFormat(geValue)}
        dateFormat={dateFormat}
        maxDate={MAX_DATE}
        minDate={null}
        errorMessages={getErrors(GREATER_OR_EQUAL)}
      />
      <Typography variant="body1" mt={1} mx={2} mb={1.25} alignSelf="center">
        —
      </Typography>
      <DateInput
        toggleButton="To: "
        source={LESS_OR_EQUAL}
        onChangeCallback={onChangeCallback}
        value={fromApiToFrontFormat(leValue)}
        maxDate={MAX_DATE}
        label=" "
        dateFormat={dateFormat}
        minDate={geValue && yearMonth(fromApiToFrontFormat(geValue))}
        errorMessages={getErrors(LESS_OR_EQUAL)}
      />
    </MonthRangeFilterLayout>
  );
};

export default MonthRangeFilter;
